import React, { createContext, useContext, useState, useCallback } from 'react';
import './ConfirmProvider.css';

const DialogContext = createContext(null);

/**
 * useDialog() — returns { confirm, notify }
 *
 *   await confirm({ title?, message, confirmLabel?, cancelLabel?, danger? })
 *     → Promise<boolean>   (true = confirmed, false = cancelled / closed)
 *
 *   await notify({ title?, message, label? })
 *     → Promise<void>      (resolves when the user dismisses the dialog)
 *
 * Use anywhere instead of window.confirm / window.alert.
 */
export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog must be used inside <ConfirmProvider>');
  return ctx;
}

export function ConfirmProvider({ children }) {
  // Active dialog descriptor; null when nothing is open
  const [dialog, setDialog] = useState(null);

  const close = useCallback((result) => {
    if (dialog && typeof dialog.resolve === 'function') dialog.resolve(result);
    setDialog(null);
  }, [dialog]);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setDialog({
        kind: 'confirm',
        title: opts.title || 'Confirm',
        message: opts.message || 'Are you sure?',
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel || 'Cancel',
        danger: !!opts.danger,
        resolve
      });
    });
  }, []);

  const notify = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setDialog({
        kind: 'notify',
        title: opts.title || 'Notice',
        message: opts.message || '',
        confirmLabel: opts.label || 'OK',
        resolve
      });
    });
  }, []);

  return (
    <DialogContext.Provider value={{ confirm, notify }}>
      {children}
      {dialog && (
        <div className="app-dialog-overlay" onClick={() => close(false)}>
          <div
            className="app-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="app-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="app-dialog-title" className="app-dialog__title">{dialog.title}</h3>
            {dialog.message && (
              <p className="app-dialog__message">{dialog.message}</p>
            )}
            <div className="app-dialog__actions">
              {dialog.kind === 'confirm' && (
                <button
                  type="button"
                  className="app-dialog__btn app-dialog__btn--secondary"
                  onClick={() => close(false)}
                  autoFocus
                >
                  {dialog.cancelLabel}
                </button>
              )}
              <button
                type="button"
                className={`app-dialog__btn ${dialog.danger ? 'app-dialog__btn--danger' : 'app-dialog__btn--primary'}`}
                onClick={() => close(true)}
                autoFocus={dialog.kind === 'notify'}
              >
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </DialogContext.Provider>
  );
}

export default ConfirmProvider;
